/**
 * ClusteringProposalModal — Kaiva proposes grouping nodes into knowledge areas
 * at a section break.
 *
 * Phase 7 redesign: matches the v1.1 modal language. Cluster previews land
 * as compact rows with a colored swatch + node count, capped at six visible
 * with a "+N more" overflow chip.
 */

import { useRef } from 'react';
import { LayoutGrid, X } from 'lucide-react';
import type { ClusterProposal } from '@/hooks/useClusterManager';
import { useEscapeClose } from '@/hooks/useEscapeClose';
import { useFocusTrap } from '@/hooks/useFocusTrap';

interface ClusteringProposalModalProps {
  proposals: ClusterProposal[];
  onApply: () => void;
  onDismiss: () => void;
}

const VISIBLE_LIMIT = 6;

// Soft cluster swatch colors that play with the v1.1 type palette.
const SWATCH_COLORS = [
  'hsl(var(--type-step))',
  'hsl(var(--type-tool))',
  'hsl(var(--type-actor))',
  'hsl(var(--type-insight))',
  'hsl(var(--type-trigger))',
  'hsl(var(--teal))',
];

export function ClusteringProposalModal({
  proposals,
  onApply,
  onDismiss,
}: ClusteringProposalModalProps) {
  useEscapeClose(onDismiss);
  const dialogRef = useRef<HTMLDivElement>(null);
  useFocusTrap(dialogRef);
  const visible = proposals.slice(0, VISIBLE_LIMIT);
  const overflow = proposals.length - visible.length;
  const totalNodes = proposals.reduce((s, p) => s + p.nodeIds.length, 0);

  return (
    <>
      <div
        className="absolute inset-0 z-50"
        onClick={onDismiss}
        style={{
          background: 'hsl(var(--foreground) / 0.18)',
          backdropFilter: 'blur(3px)',
          animation: 'fadeIn 180ms ease-out',
        }}
      />

      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label="Organize into knowledge areas"
        className="absolute left-1/2 top-1/2 z-[51] overflow-hidden"
        style={{
          width: 380,
          maxWidth: 'calc(100vw - 40px)',
          background: 'hsl(var(--background))',
          borderRadius: 16,
          boxShadow: 'var(--shadow-3), 0 0 0 1px hsl(var(--border))',
          transform: 'translate(-50%, -50%)',
          animation: 'ctxIn 220ms cubic-bezier(0.2, 0.8, 0.2, 1)',
        }}
      >
        {/* Header */}
        <div
          className="flex items-start justify-between gap-3 px-5 pb-4 pt-5"
          style={{ borderBottom: '1px solid hsl(var(--border))' }}
        >
          <div className="min-w-0 flex-1">
            <div
              className="mb-1 inline-flex items-center gap-1.5 kv-eyebrow kv-eyebrow--accent"
              style={{
                letterSpacing: '0.1em',
              }}
            >
              <LayoutGrid className="h-2.5 w-2.5" />
              Canvas · Organize
            </div>
            <div
              style={{
                fontSize: 16,
                fontWeight: 600,
                color: 'hsl(var(--foreground))',
                letterSpacing: '-0.01em',
                lineHeight: 1.3,
              }}
            >
              Group these into {proposals.length} area
              {proposals.length === 1 ? '' : 's'}?
            </div>
            <div
              className="mt-1"
              style={{ fontSize: 12, color: 'hsl(var(--muted-foreground))' }}
            >
              {totalNodes} node{totalNodes === 1 ? '' : 's'} · current layout stays
              if you decline.
            </div>
          </div>
          <button
            type="button"
            onClick={onDismiss}
            aria-label="Dismiss"
            className="inline-grid h-[28px] w-[28px] place-items-center rounded-lg transition-colors hover:[background:hsl(var(--muted))]"
            style={{
              background: 'hsl(var(--card))',
              border: '1px solid hsl(var(--border))',
              color: 'hsl(var(--muted-foreground))',
            }}
          >
            <X className="h-3 w-3" />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4">
          <div className="flex flex-col gap-1.5">
            {visible.map((p, i) => (
              <ClusterRow
                key={p.id}
                name={p.name}
                nodeCount={p.nodeIds.length}
                color={SWATCH_COLORS[i % SWATCH_COLORS.length]}
              />
            ))}
            {overflow > 0 && (
              <div
                className="rounded-[10px] px-3 py-2 text-center kv-eyebrow"
                style={{
                  fontSize: 11,
                  color: 'hsl(var(--muted-foreground))',
                  background: 'hsl(var(--muted) / 0.35)',
                  border: '1px dashed hsl(var(--border))',
                  letterSpacing: '0.04em',
                }}
              >
                +{overflow} more
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div
          className="flex items-center justify-end gap-2 px-5 py-4"
          style={{ borderTop: '1px solid hsl(var(--border))' }}
        >
          <button
            type="button"
            onClick={onDismiss}
            className="transition-colors hover:[background:hsl(var(--muted))]"
            style={{
              height: 32,
              padding: '0 14px',
              borderRadius: 8,
              background: 'hsl(var(--card))',
              color: 'hsl(var(--foreground))',
              border: '1px solid hsl(var(--border))',
              fontSize: 12.5,
              fontWeight: 500,
            }}
          >
            Keep current layout
          </button>
          <button
            type="button"
            onClick={onApply}
            className="transition-colors"
            style={{
              height: 32,
              padding: '0 14px',
              borderRadius: 8,
              background: 'hsl(var(--sienna))',
              color: 'hsl(var(--cream))',
              border: '1px solid hsl(var(--sienna))',
              fontSize: 12.5,
              fontWeight: 500,
            }}
          >
            Apply organization
          </button>
        </div>
      </div>
    </>
  );
}

function ClusterRow({
  name,
  nodeCount,
  color,
}: {
  name: string;
  nodeCount: number;
  color: string;
}) {
  return (
    <div
      className="flex items-center justify-between gap-3 rounded-[10px] px-3 py-2"
      style={{
        background: 'hsl(var(--muted) / 0.35)',
        border: '1px solid hsl(var(--border))',
      }}
    >
      <div className="flex min-w-0 items-center gap-2.5">
        <span
          aria-hidden="true"
          className="inline-block flex-shrink-0 rounded-[3px]"
          style={{ width: 9, height: 9, background: color }}
        />
        <span
          className="truncate"
          style={{
            fontSize: 12.5,
            fontWeight: 500,
            color: 'hsl(var(--foreground))',
          }}
        >
          {name}
        </span>
      </div>
      <span
        className="flex-shrink-0 tabular-nums"
        style={{
          fontSize: 10.5,
          color: 'hsl(var(--muted-foreground))',
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
        }}
      >
        {nodeCount} node{nodeCount === 1 ? '' : 's'}
      </span>
    </div>
  );
}
